/*
 * Viewport-filling "stage" for kiosk content.
 *
 * Classy runs on every large display from a 1024x768 projector to a
 * 3840x2160 Promethean board, in any aspect ratio. We deliberately do
 * NOT design at a fixed 1920x1080 canvas and transform-scale it — that
 * letterboxed non-16:9 displays and made centered page content collide
 * with the edge chrome (which is anchored to the real viewport). Instead
 * the stage simply fills the viewport and pages lay out fluidly, sized
 * off the global --ui-scale knob (see @classy/design-tokens tokens.css).
 *
 * Two modes:
 *   default  — fixed (non-scrolling) full-viewport surface; centers its
 *              child within a comfortable edge pad. Used by the hub and
 *              the live morning-meeting surface.
 *   scroll   — teacher-config screens that may exceed one screen; flows
 *              naturally and scrolls vertically.
 */

.viewport {
  position: fixed;
  inset: 0;
  overflow: hidden;
  /* Above the BgLayer (z:0) and the global top-edge scrim (z:1, on .app::before). */
  z-index: 2;
  pointer-events: none;
}

.stage {
  position: absolute;
  inset: 0;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  /* Keep content off the physical screen edge. The hub has no live
   * chrome so the edge pad is enough. */
  padding: var(--token-edge-pad);
}

/*
 * Live morning-meeting variant. Reserves the fuller safe area so page
 * content centers clear of the fixed edge chrome — the top-left back
 * button + corner mascot, the left/right page chevrons, and the bottom
 * nav / clock / fullscreen cluster — and clears TV overscan.
 */
.stageLive {
  padding: var(--token-safe-top) var(--token-safe-x) var(--token-safe-bottom);
}

/*
 * Scroll mode — teacher-config screens that may exceed one screen.
 * Content flows naturally (the config pages are responsive via their
 * own clamp/% CSS) and the viewport scrolls vertically. Mirrors the
 * .app--admin pattern.
 */
.scrollViewport {
  position: fixed;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  /* Above the BgLayer (z:0) and the global top-edge scrim (z:1). */
  z-index: 2;
}

.scrollStage {
  width: 100%;
  /* Fill the viewport when content is short so the bg shows below; grow
   * past it (and scroll) when content is tall. */
  min-height: 100%;
  padding: var(--token-edge-pad);
}
